/**
 * TasksPage.tsx — Página de Tareas con vista Kanban + Lista
 *
 * Filtros: lote, prioridad, tipo de actividad, fecha
 * Vistas: Kanban (por estado) / Lista (tabla ordenable)
 * Actualización de estado: drag-free — dropdown por tarjeta
 */

import { DataTable } from '../components/shared/DataTable';
import { PageTitle } from '../components/shared/PageTitle';
import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState, useMemo } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { DateInput } from '../components/shared/DateInput';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, Dropdown, Option, Badge, Spinner, Text,
  Input, Tooltip, tokens, makeStyles,
  Card, Field, Textarea,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import {
  TableRegular, ViewDesktop24Regular,
  CheckmarkCircle24Regular, CheckmarkCircle24Filled, Clock24Regular,
  Dismiss24Regular, Circle24Regular, ChevronDown20Regular, ChevronRight20Regular,
  FilterRegular, EditRegular, DeleteRegular,
  ChatSparkle20Regular,
} from '@fluentui/react-icons';
import { fetchTasks, updateTaskStatus, createTask, updateTask, deleteTask, fetchAssignees, recomputeTaskRecipients, type CreateTaskInput } from '../api/dashboard.api';
import { TaskAudienceSection, type AudienceValue } from '../components/tasks/TaskAudienceSection';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { fetchModules } from '../api/modules.api';
import { parseLoteZoneId, loteZoneId, zoneWithDescendantIds } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { orderedModules } from '../utils/module-tree';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { fetchRanchos } from '../api/rancho.api';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { StatusBadge } from '../components/shared/StatusBadge';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useRanchoToday } from '../hooks/useRanchoToday';
import { isTaskOverdue } from '../utils/task-overdue';
import type { Task, TaskStatus, TaskPriority, ActivityType } from '../types/api.types';

// ─── Estilos ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    padding: '28px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  toolbar: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '20px',
  },
  viewToggle: {
    display: 'flex',
    gap: '4px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '6px',
    padding: '2px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  viewBtn: {
    minWidth: '36px',
    padding: '4px 8px',
  },
  // ── Kanban ──
  kanbanBoard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '16px',
    alignItems: 'start',
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(4, minmax(240px, 1fr))',
      overflowX: 'auto',
      paddingBottom: '8px',
    },
  },
  column: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '10px',
    padding: '12px',
    minHeight: '200px',
    border: '2px solid transparent',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
  },
  columnDragOver: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontWeight: '600',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  columnCount: {
    fontSize: '11px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '10px',
    padding: '1px 6px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '700',
  },
  taskCard: {
    marginBottom: '10px',
    cursor: 'default',
    borderLeft: '3px solid transparent',
  },
  taskCardDraggable: {
    cursor: 'grab',
  },
  taskCardDragging: {
    opacity: 0.4,
  },
  taskCardHigh: {
    borderLeftColor: '#E53E3E',
  },
  taskCardMedium: {
    borderLeftColor: '#E8A020',
  },
  taskCardLow: {
    borderLeftColor: '#38A169',
  },
  taskTitle: {
    fontWeight: '600',
    fontSize: '13px',
    marginBottom: '4px',
  },
  taskMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '6px',
  },
  // ── Lista ──
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: tokens.colorNeutralForeground3,
  },
  // ── Acordeón de la vista Lista (agrupado por estado, no por categoría) ──
  accordionSection: {
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  },
  accordionHeaderPending: {
    backgroundColor: 'rgba(232, 160, 32, 0.14)',
    color: '#8A5A00',
  },
  accordionHeaderCompleted: {
    backgroundColor: 'rgba(56, 161, 105, 0.14)',
    color: '#1E5E3A',
  },
  accordionCount: {
    fontSize: '11px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '10px',
    padding: '1px 8px',
    fontWeight: '700',
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_ORDER: TaskStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

const STATUS_ICON: Record<TaskStatus, JSX.Element> = {
  PENDING:     <Circle24Regular style={{ color: '#718096', fontSize: '16px' }} />,
  IN_PROGRESS: <Clock24Regular style={{ color: '#E8A020', fontSize: '16px' }} />,
  COMPLETED:   <CheckmarkCircle24Regular style={{ color: '#38A169', fontSize: '16px' }} />,
  CANCELLED:   <Dismiss24Regular style={{ color: '#E53E3E', fontSize: '16px' }} />,
};

// Solo el color es constante — el label viene de i18n dentro del componente
const PRIORITY_COLOR: Record<TaskPriority, 'danger' | 'warning' | 'success'> = {
  HIGH:   'danger',
  MEDIUM: 'warning',
  LOW:    'success',
};

// Tag de color por tipo de actividad — mejora el escaneo visual de listas largas
const TYPE_COLOR: Record<ActivityType, 'brand' | 'success' | 'warning' | 'danger' | 'informative' | 'severe' | 'important' | 'subtle'> = {
  PREPARACION:    'subtle',
  SIEMBRA:        'success',
  RIEGO:          'informative',
  FERTILIZACION:  'important',
  FITOSANITARIO:  'severe',
  COSECHA:        'brand',
  MAQUINARIA:     'warning',
  LABORES:        'subtle',
  OTRO:           'informative',
  NOVEDAD:        'important',
  CLIMA:          'informative',
};

function priorityBorderClass(priority: TaskPriority, s: ReturnType<typeof useStyles>): string {
  if (priority === 'HIGH')   return s.taskCardHigh;
  if (priority === 'MEDIUM') return s.taskCardMedium;
  return s.taskCardLow;
}

/** Valor del selector de lote para "Sin lote (tarea general)". */
export const NO_LOTE = '__GENERAL__';
export const isGeneralTask = (task: Pick<Task, 'loteId' | 'isGeneral'>): boolean =>
  !!task.isGeneral || (task.loteId ?? '').startsWith('GENERAL#');

// ─── TaskFormDialog (crear / editar) ──────────────────────────────────────────

interface TaskFormProps {
  open:    boolean;
  onClose: () => void;
  initial?: Task | null;
  lotes:   { loteId: string; name: string }[];
}

type TForm = {
  loteId: string; moduleId: string; ranchoId: string; title: string; description: string; type: ActivityType;
  scheduledDate: string; dueDate: string; priority: TaskPriority; assignedTo: string;
  /** datetime-local (hora del navegador); vacío = avisar ahora. Solo al crear. */
  notifyAt: string;
};

/** Valor del selector de zona para "Todo el lote" (no se envía moduleId). */
const ZONE_ALL_LOTE = '__ALL_LOTE__';

const EMPTY_TASK_FORM: TForm = {
  loteId: '', moduleId: '', ranchoId: '', title: '', description: '', type: 'OTRO',
  scheduledDate: '', dueDate: '', priority: 'MEDIUM', assignedTo: '', notifyAt: '', // se llena con useRanchoToday() al abrir
};

function toTaskForm(task: Task): TForm {
  return {
    loteId: task.loteId, moduleId: '', ranchoId: '', title: task.title, description: task.description ?? '',
    type: task.type, scheduledDate: task.scheduledDate, dueDate: task.dueDate ?? '',
    priority: task.priority, assignedTo: task.assignedTo ?? '', notifyAt: '',
  };
}

function TaskFormDialog({ open, onClose, initial, lotes }: TaskFormProps) {
  const t  = useT();
  const { fmtDateTime } = useIntlFormat();
  const qc = useQueryClient();
  const isEdit = !!initial;
  const todayFn = useRanchoToday();
  const activeRanchoId = useRanchoStore(s => s.activeRanchoId);

  const [form,  setForm]  = useState<TForm>(initial ? toTaskForm(initial) : { ...EMPTY_TASK_FORM, scheduledDate: todayFn() });
  const [error, setError] = useState('');
  const [cancelNotice, setCancelNotice] = useState(false);
  const [telegramHelpOpen, setTelegramHelpOpen] = useState(false);
  const [aud, setAud] = useState<AudienceValue | null>(null);

  const fe = useFieldErrors();

  useResetOnOpen(open, () => {
    setForm(initial ? toTaskForm(initial) : { ...EMPTY_TASK_FORM, scheduledDate: todayFn() });
    setError(''); fe.clear();
    setAud(null);
  });

  const { data: assignees = [] } = useQuery({
    queryKey: ['assignees'],
    queryFn:  fetchAssignees,
    enabled:  open,
  });

  const set = (patch: Partial<TForm>) => {
    setForm(f => ({ ...f, ...patch }));
    Object.keys(patch).forEach(k => fe.clear(k));
  };

  const isNoLote = form.loteId === NO_LOTE || (isEdit && isGeneralTask(initial!));
  const { data: ranchos = [] } = useQuery({
    queryKey: ['ranchos'],
    queryFn:  fetchRanchos,
    enabled:  open && isNoLote && !isEdit && !activeRanchoId,
  });
  const effectiveRanchoId = activeRanchoId || form.ranchoId;

  // Zonas del lote elegido (solo al crear: la edición no cambia la ubicación de una tarea existente).
  const zonesQ = useQuery({
    queryKey: ['zones-lote', form.loteId],
    queryFn:  () => fetchModules({ loteId: form.loteId }),
    enabled:  open && !isEdit && !!form.loteId && form.loteId !== NO_LOTE,
  });

  const activityTypes: ActivityType[] = [
    'PREPARACION', 'SIEMBRA', 'RIEGO', 'FERTILIZACION',
    'FITOSANITARIO', 'COSECHA', 'MAQUINARIA', 'LABORES', 'OTRO',
  ];
  const activityLabel = t.activityTypes as Record<string, string>;

  const createMut = useMutation({
    mutationFn: () => {
      const input: CreateTaskInput = {
        ...(form.loteId === NO_LOTE ? { ranchoId: effectiveRanchoId } : { loteId: form.loteId, ...(form.moduleId ? { moduleId: form.moduleId } : {}) }),
        title: form.title.trim(), description: form.description.trim() || undefined,
        type: form.type, scheduledDate: form.scheduledDate, dueDate: form.dueDate || undefined,
        priority: form.priority, assignedTo: form.assignedTo.trim() || undefined,
        // Hora local sin zona: el backend la interpreta en la zona horaria del rancho.
        ...(form.notifyAt ? { notifyAt: form.notifyAt } : {}),
        ...(aud ? { audience: aud.audience, recipients: aud.recipients } : {}),
      };
      return createTask(input);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.tasks.createTaskError),
  });

  const updateMut = useMutation({
    mutationFn: () => updateTask(initial!.taskId, {
      loteId: initial!.loteId, title: form.title.trim(), description: form.description.trim() || undefined,
      scheduledDate: form.scheduledDate, dueDate: form.dueDate || undefined,
      priority: form.priority, assignedTo: form.assignedTo.trim() || undefined,
      ...(aud ? { audience: aud.audience, recipients: aud.recipients } : {}),
      ...(cancelNotice ? { notifyAt: null } : form.notifyAt ? { notifyAt: form.notifyAt } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.tasks.updateTaskError),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      loteId: !isEdit && !form.loteId && t.tasks.loteRequired,
      ranchoId: !isEdit && form.loteId === NO_LOTE && !effectiveRanchoId && t.tasks.ranchoRequired,
      title: !form.title.trim() && t.tasks.titleRequired,
      scheduledDate: !form.scheduledDate && t.tasks.dateRequired,
      audience: !!aud && aud.recipients.length === 0 && t.tasks.audNoRecipientsError,
    });
    if (!ok) return;
    isEdit ? updateMut.mutate() : createMut.mutate();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="tasks"
              voiceExample={t.telegramHelp.tasksVoice}
              voiceDesc={t.telegramHelp.tasksDesc}
            />
          }>{isEdit ? t.tasks.editTaskTitle : t.tasks.newTaskTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Field label={t.tasks.fieldLote} {...fe.props('loteId')}>
                <Dropdown
                  selectedOptions={form.loteId ? [form.loteId] : []}
                  value={isNoLote ? t.tasks.noLoteOption : (lotes.find(l => l.loteId === form.loteId)?.name ?? '')}
                  onOptionSelect={(_, d) => set({ loteId: (d.optionValue as string) ?? '', moduleId: '' })}
                  disabled={saving || isEdit}
                >
                  <Option key={NO_LOTE} value={NO_LOTE}>{t.tasks.noLoteOption}</Option>
                  {lotes.map(l => (
                    <Option key={l.loteId} value={l.loteId}>{l.name}</Option>
                  ))}
                </Dropdown>
              </Field>

              {!isNoLote && !isEdit && (
                <Field label={t.tasks.fieldZone}>
                  <Dropdown
                    selectedOptions={[form.moduleId || ZONE_ALL_LOTE]}
                    value={(() => {
                      const zone = (zonesQ.data ?? []).find(z => z.moduleId === form.moduleId);
                      return zone ? (zone.name ?? zone.moduleCode) : t.tasks.zoneAllLote;
                    })()}
                    onOptionSelect={(_, d) => set({ moduleId: d.optionValue === ZONE_ALL_LOTE ? '' : (d.optionValue ?? '') })}
                    disabled={saving || zonesQ.isLoading}
                  >
                    <Option key={ZONE_ALL_LOTE} value={ZONE_ALL_LOTE}>{t.tasks.zoneAllLote}</Option>
                    {orderedModules(zonesQ.data ?? []).map(({ mod: z, depth }) => (
                      <Option key={z.moduleId} value={z.moduleId}>{(depth ? '↳ ' : '') + (z.name ?? z.moduleCode)}</Option>
                    ))}
                  </Dropdown>
                </Field>
              )}

              {isNoLote && !isEdit && !activeRanchoId && (
                <Field label={t.tasks.fieldRancho} {...fe.props('ranchoId')}>
                  <Dropdown
                    selectedOptions={form.ranchoId ? [form.ranchoId] : []}
                    value={ranchos.find(r => r.ranchoId === form.ranchoId)?.name ?? ''}
                    onOptionSelect={(_, d) => set({ ranchoId: (d.optionValue as string) ?? '' })}
                    disabled={saving}
                  >
                    {ranchos.map(r => (
                      <Option key={r.ranchoId} value={r.ranchoId}>{r.name}</Option>
                    ))}
                  </Dropdown>
                </Field>
              )}

              <Field label={t.tasks.fieldTaskTitle} {...fe.props('title')}>
                <Input value={form.title} onChange={(_, d) => set({ title: d.value })} placeholder={t.tasks.fieldTaskTitlePlaceholder} disabled={saving} />
              </Field>

              <Field label={t.tasks.fieldDescription}>
                <Textarea value={form.description} onChange={(_, d) => set({ description: d.value })} disabled={saving} />
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                {!isNoLote && <Field label={t.tasks.fieldActivityType}>
                  <Dropdown
                    selectedOptions={[form.type]}
                    value={activityLabel[form.type] ?? form.type}
                    onOptionSelect={(_, d) => set({ type: d.optionValue as ActivityType })}
                    disabled={saving || isEdit}
                  >
                    {activityTypes.map(type => (
                      <Option key={type} value={type}>{activityLabel[type]}</Option>
                    ))}
                  </Dropdown>
                </Field>}
                <Field label={t.tasks.fieldPriority}>
                  <Dropdown
                    selectedOptions={[form.priority]}
                    value={(t.taskPriority as Record<string, string>)[form.priority]}
                    onOptionSelect={(_, d) => set({ priority: d.optionValue as TaskPriority })}
                    disabled={saving}
                  >
                    <Option value="HIGH">{t.taskPriority.HIGH}</Option>
                    <Option value="MEDIUM">{t.taskPriority.MEDIUM}</Option>
                    <Option value="LOW">{t.taskPriority.LOW}</Option>
                  </Dropdown>
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={t.tasks.fieldScheduledDate} {...fe.props('scheduledDate')}>
                  <DateInput value={form.scheduledDate} onValueChange={(v) => set({ scheduledDate: v })} disabled={saving} />
                </Field>
                <Field label={t.tasks.fieldDueDate}>
                  <DateInput value={form.dueDate} onValueChange={(v) => set({ dueDate: v })} disabled={saving} />
                </Field>
              </div>

              <Field label={t.tasks.fieldAssignedTo}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <Dropdown
                    style={{ flex: 1 }}
                    selectedOptions={form.assignedTo ? [form.assignedTo] : []}
                    value={assignees.find(a => a.id === form.assignedTo)?.label ?? ''}
                    onOptionSelect={(_, d) => set({ assignedTo: (d.optionValue as string) ?? '' })}
                    disabled={saving}
                    placeholder={t.tasks.assignedToPlaceholder}
                  >
                    {assignees.filter(a => a.kind === 'USER').map(a => (
                      <Option key={a.id} value={a.id} text={a.label}>
                        {a.label}{a.sub ? ` — ${a.sub}` : ''}
                      </Option>
                    ))}
                  </Dropdown>
                  <Tooltip content={t.tasks.telegramHelpTooltip} relationship="description">
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<ChatSparkle20Regular aria-label={t.a11yLabels.telegram} />}
                      onClick={() => setTelegramHelpOpen(true)}
                    />
                  </Tooltip>
                </div>
                {assignees.length === 0 && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {t.tasks.noAssigneesHint}
                  </Text>
                )}
              </Field>

              {/* Aviso programado: al crear, o al editar mientras aún no ha salido. */}
              {(!isEdit || (initial && !initial.notifiedAt)) && (
                <Field label={t.tasks.fieldNotifyAt} hint={t.tasks.notifyAtHint}>
                  {isEdit && initial?.notifyAt && !cancelNotice && (
                    <Text size={200} style={{ display: 'block', marginBottom: '4px' }}>
                      {t.tasks.notifyScheduledLabel}: {fmtDateTime(initial.notifyAt)}
                    </Text>
                  )}
                  {cancelNotice ? (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.tasks.notifyCancelledHint}</Text>
                  ) : (
                    <Input
                      type="datetime-local"
                      value={form.notifyAt}
                      onChange={(_, d) => set({ notifyAt: d.value })}
                      disabled={saving}
                    />
                  )}
                  {isEdit && initial?.notifyAt && (
                    <Button size="small" appearance="subtle" onClick={() => setCancelNotice(v => !v)} disabled={saving} style={{ marginTop: '4px' }}>
                      {cancelNotice ? t.tasks.notifyKeepButton : t.tasks.notifyCancelButton}
                    </Button>
                  )}
                </Field>
              )}

              {(!isEdit || initial?.audience) && (
                <TaskAudienceSection
                  loteId={isEdit ? initial!.loteId : (form.loteId && form.loteId !== NO_LOTE ? form.loteId : undefined)}
                  ranchoId={!isEdit && form.loteId === NO_LOTE ? effectiveRanchoId : undefined}
                  lotes={lotes}
                  disabled={saving}
                  initial={initial?.audience ? { audience: initial.audience, recipients: (initial.recipients ?? []).map(r => r.phoneNumber) } : null}
                  onChange={(v) => { setAud(v); fe.clear('audience'); }}
                />
              )}
              {fe.errors.audience && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{fe.errors.audience}</Text>}

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={saving}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {t.common.save}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <TelegramHelpDialog open={telegramHelpOpen} onClose={() => setTelegramHelpOpen(false)} />
    </>
  );
}

// ─── TelegramHelpDialog ────────────────────────────────────────────────────────

function TelegramHelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const tg = t.tasks;
  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle>{tg.telegramHelpTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {tg.telegramHelpIntro}
              </Text>

              <div style={{ backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium, padding: '10px' }}>
                <Text weight="semibold" size={200}>{tg.telegramHelpCommandLabel}</Text>
                <Text block style={{ fontFamily: 'monospace', fontSize: '13px', marginTop: '4px' }}>/start</Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{tg.telegramHelpCommandDesc}</Text>
              </div>

              <div style={{ backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium, padding: '10px' }}>
                <Text weight="semibold" size={200}>{tg.telegramHelpVoiceLabel}</Text>
                <Text block italic style={{ fontSize: '13px', marginTop: '4px' }}>"{tg.telegramHelpVoiceExample}"</Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{tg.telegramHelpVoiceDesc}</Text>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>{t.tasks.confirmDialog}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── DeleteTaskDialog ──────────────────────────────────────────────────────────

function DeleteTaskDialog({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const t  = useT();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => deleteTask(task!.taskId, task!.loteId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); onClose(); },
  });

  return (
    <Dialog open={!!task} onOpenChange={(_, d) => !d.open && onClose()} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t.tasks.deleteTaskTitle}</DialogTitle>
          <DialogContent><Text>{t.tasks.deleteTaskConfirm}</Text></DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.common.delete}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function TasksPage() {
  const todayStr = useRanchoToday()();
  const s = useStyles();
  const qc = useQueryClient();
  const t = useT();
  const { user } = useAuthStore();
  // Roles con permiso lotes:write (ver DEFAULT_ROLE_PERMISSIONS en rbac.model.ts) —
  // crear/editar/eliminar tareas requiere el mismo nivel que editar el lote (ver tasks.handler.ts).
  const canWrite = user?.roles.some(r => ['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR'].includes(r)) ?? false;
  const { formatDate } = useLocaleSettings();

  // ── Estado local ─────────────────────────────────────────────────────────────
  const [view, setView]         = useState<'kanban' | 'list'>('kanban');
  const [loteFilter, setLote]   = useState<string>('');
  const [prioFilter, setPrio]   = useState<string>('');
  const [typeFilter, setType]   = useState<string>('');
  const [searchText, setSearch] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [pendingCollapsed, setPendingCollapsed]     = useState(false);
  const [completedCollapsed, setCompletedCollapsed] = useState(false);
  // ── Drag & drop entre columnas del Kanban ──
  const [draggedTaskId, setDraggedTaskId]   = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TaskStatus | null>(null);

  // ── Queries ───────────────────────────────────────────────────────────────────
  const { activeRanchoId } = useRanchoStore();
  const { data: allTasks = [], isLoading: loadingTasks } = useQuery({
    queryKey: ['tasks', activeRanchoId],
    queryFn:  () => fetchTasks(undefined, activeRanchoId),
  });

  const { lotes } = useRanchoLotes(true, { value: loteFilter, reset: () => setLote('') });
  const { data: modules = [] } = useQuery({
    queryKey: ['modules-rancho', activeRanchoId],
    queryFn:  () => fetchModules({ ranchoId: activeRanchoId }),
  });
  // Filtro de zona: id con prefijo del árbol (L:/M:). Una zona fija también su lote.
  const [zoneFilter, setZoneFilter] = useState('');
  const zoneOptions = useLoteZoneOptions(lotes, modules);

  // ── Mutation ──────────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: ({ taskId, loteId, status }: { taskId: string; loteId: string; status: string }) =>
      updateTaskStatus(taskId, loteId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const [notice, setNotice] = useState('');
  const recomputeMut = useMutation({
    mutationFn: (task: Task) => recomputeTaskRecipients(task.taskId, task.loteId),
    onSuccess: (r) => {
      setNotice(r.added > 0 ? t.tasks.audRecomputed.replace('{n}', String(r.added)) : t.tasks.audRecomputeNone);
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: () => setNotice(t.tasks.audRecomputeError),
  });

  // ── Filtrado ──────────────────────────────────────────────────────────────────
  const filterId = zoneFilter || (loteFilter ? loteZoneId({ loteId: loteFilter }) : '');
  const zoneSel = zoneFilter ? parseLoteZoneId(zoneFilter, modules) : null;
  // Zona elegida: sus tareas y las de sus subzonas (por moduleId). Solo lote: todas las del lote.
  const zoneIds = useMemo(
    () => (zoneSel?.moduleId ? zoneWithDescendantIds(zoneSel.moduleId, modules) : null),
    [zoneSel?.moduleId, modules],
  );
  const tasks = useMemo(() => {
    return allTasks.filter(t => {
      if (zoneIds) { if (!t.moduleId || !zoneIds.has(t.moduleId)) return false; }
      else if (loteFilter && t.loteId !== loteFilter)     return false;
      if (prioFilter && t.priority !== prioFilter)        return false;
      if (typeFilter && t.type !== typeFilter)            return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!t.title.toLowerCase().includes(q) && !(t.description ?? '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allTasks, loteFilter, zoneIds, prioFilter, typeFilter, searchText]);

  const byStatus = useMemo(() => {
    const map: Record<TaskStatus, Task[]> = { PENDING: [], IN_PROGRESS: [], COMPLETED: [], CANCELLED: [] };
    for (const t of tasks) map[t.status].push(t);
    return map;
  }, [tasks]);

  // Agrupación de la vista Lista en 2 acordeones por estado (Pendientes/Completados) —
  // distinto del agrupado por categoría (StatusBadge de tipo de actividad, que se mantiene intacto)
  const sortedTasks = useMemo(
    () => tasks.slice().sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)),
    [tasks],
  );
  const pendingTasks   = useMemo(() => sortedTasks.filter(t => t.status === 'PENDING' || t.status === 'IN_PROGRESS'), [sortedTasks]);
  const completedTasks = useMemo(() => sortedTasks.filter(t => t.status === 'COMPLETED' || t.status === 'CANCELLED'), [sortedTasks]);

  const loteName = (id: string) =>
    id.startsWith('GENERAL#') ? t.tasks.generalLabel : (lotes.find(l => l.loteId === id)?.name ?? id);

  // ── Activity types para filtro ────────────────────────────────────────────────
  const activityTypes: ActivityType[] = [
    'PREPARACION', 'SIEMBRA', 'RIEGO', 'FERTILIZACION',
    'FITOSANITARIO', 'COSECHA', 'MAQUINARIA', 'LABORES', 'OTRO',
  ];

  // Alias local para legibilidad — usa las traducciones del i18n activo
  const activityLabel = t.activityTypes as Record<string, string>;
  const statusLabel   = t.taskStatus as Record<string, string>;

  // Etiqueta de audiencia + avance "3/8" (y boton de recalcular para quien gestiona)
  const audienceKindLabel: Record<string, string> = {
    ALL: t.tasks.audAll, ROLES: t.tasks.audRoles, LOTES: t.tasks.audLotes, CROPS: t.tasks.audCrops,
  };
  const AudienceTag = ({ task }: { task: Task }) => {
    if (!task.audience) return null;
    const p = task.recipientProgress ?? { done: (task.recipients ?? []).filter(r => r.status === 'COMPLETED').length, total: (task.recipients ?? []).length };
    return (
      <>
        <Badge appearance="tint" color="brand" size="small" data-testid="audience-badge">
          {audienceKindLabel[task.audience.type]} · {p.done}/{p.total}
        </Badge>
        {canWrite && (
          <Button size="small" appearance="subtle" disabled={recomputeMut.isPending} onClick={() => recomputeMut.mutate(task)}>
            {t.tasks.audRecompute}
          </Button>
        )}
      </>
    );
  };

  // ── Task card ─────────────────────────────────────────────────────────────────
  const TaskCard = ({ task }: { task: Task }) => (
    <Card
      className={[
        s.taskCard,
        priorityBorderClass(task.priority, s),
        canWrite ? s.taskCardDraggable : '',
        draggedTaskId === task.taskId ? s.taskCardDragging : '',
      ].filter(Boolean).join(' ')}
      draggable={canWrite}
      onDragStart={canWrite ? (e) => {
        setDraggedTaskId(task.taskId);
        e.dataTransfer.effectAllowed = 'move';
      } : undefined}
      onDragEnd={canWrite ? () => {
        setDraggedTaskId(null);
        setDragOverStatus(null);
      } : undefined}
      size="small"
    >
      <div className={s.taskTitle}>{task.title}</div>
      {task.description && (
        <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
          {task.description}
        </Text>
      )}
      <div className={s.taskMeta}>
        <span>📍 {loteName(task.loteId)}</span>
        <span>📅 {formatDate(task.scheduledDate)}</span>
        {isTaskOverdue(task, todayStr) && <span style={{ color: '#E53E3E', fontWeight: '600' }}>⚠️ {t.tasks.overdueBadge}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
        <StatusBadge value={task.type} label={activityLabel[task.type] ?? task.type} colorMap={TYPE_COLOR} size="small" />
        <Badge color={PRIORITY_COLOR[task.priority]} size="small">
          {(t.taskPriority as Record<string,string>)[task.priority]}
        </Badge>
        {task.relatedShipmentId && (
          <Badge appearance="tint" size="small">{t.tasks.linkedShipment}</Badge>
        )}
        {task.relatedPayrollEntryId && (
          <Badge appearance="tint" size="small">{t.tasks.linkedPayroll}</Badge>
        )}
        <AudienceTag task={task} />
        <Dropdown
          size="small"
          value={statusLabel[task.status]}
          selectedOptions={[task.status]}
          onOptionSelect={(_, d) => {
            if (d.optionValue && d.optionValue !== task.status) {
              mutation.mutate({ taskId: task.taskId, loteId: task.loteId, status: d.optionValue });
            }
          }}
          style={{ minWidth: '130px', fontSize: '11px' }}
        >
          {STATUS_ORDER.map(st => (
            <Option key={st} value={st}>{statusLabel[st]}</Option>
          ))}
        </Dropdown>
        {canWrite && (
          <>
            <Button size="small" appearance="subtle" icon={<EditRegular />} onClick={() => { setEditTask(task); setFormOpen(true); }} />
            <Button size="small" appearance="subtle" icon={<DeleteRegular />} style={{ color: tokens.colorStatusDangerForeground1 }} onClick={() => setDeleteTarget(task)} />
          </>
        )}
      </div>
    </Card>
  );

  // ── Tabla de la vista Lista (reusada por ambos acordeones) ───────────────────
  const TaskListTable = ({ rows }: { rows: Task[] }) => (
    <DataTable<Task>
      rows={rows}
      rowKey={(task) => task.taskId}
      columns={[
        { key: 'title', header: tt.tableTitle, width: 2, render: (task) => (
          <>
            <div style={{ fontWeight: 600 }}>
              {task.title}
              {isTaskOverdue(task, todayStr) && (
                <span style={{ color: '#E53E3E', fontSize: '11px', marginLeft: '6px' }}>{tt.overdueBadge}</span>
              )}
            </div>
            {task.description && (
              <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{task.description}</div>
            )}
          </>
        ) },
        { key: 'lot', header: tt.tableLot, render: (task) => loteName(task.loteId) },
        { key: 'type', header: tt.tableType, render: (task) => (
          <StatusBadge value={task.type} label={activityLabel[task.type] ?? task.type} colorMap={TYPE_COLOR} size="small" />
        ) },
        { key: 'priority', header: tt.tablePriority, width: 1.4, render: (task) => (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <Badge color={PRIORITY_COLOR[task.priority]} size="small">
              {(t.taskPriority as Record<string, string>)[task.priority]}
            </Badge>
            {task.relatedShipmentId && <Badge appearance="tint" size="small">{t.tasks.linkedShipment}</Badge>}
            {task.relatedPayrollEntryId && <Badge appearance="tint" size="small">{t.tasks.linkedPayroll}</Badge>}
            <AudienceTag task={task} />
          </div>
        ) },
        { key: 'date', header: tt.tableDate, render: (task) => formatDate(task.scheduledDate) },
        { key: 'status', header: tt.tableStatus, width: 1.6, render: (task) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip content={task.status === 'COMPLETED' ? tt.alreadyCompleted : tt.markComplete} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                disabled={task.status === 'COMPLETED'}
                icon={task.status === 'COMPLETED'
                  ? <CheckmarkCircle24Filled style={{ color: '#38A169', fontSize: '16px' }} />
                  : <CheckmarkCircle24Regular style={{ fontSize: '16px' }} />}
                onClick={() => mutation.mutate({ taskId: task.taskId, loteId: task.loteId, status: 'COMPLETED' })}
              />
            </Tooltip>
            <Dropdown
              size="small"
              value={statusLabel[task.status]}
              selectedOptions={[task.status]}
              onOptionSelect={(_, d) => {
                if (d.optionValue && d.optionValue !== task.status) {
                  mutation.mutate({ taskId: task.taskId, loteId: task.loteId, status: d.optionValue });
                }
              }}
              style={{ minWidth: '130px' }}
            >
              {STATUS_ORDER.map(st => (
                <Option key={st} value={st}>{statusLabel[st]}</Option>
              ))}
            </Dropdown>
          </div>
        ) },
        ...(canWrite ? [{ key: 'actions', header: t.common.actions, render: (task: Task) => (
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button size="small" appearance="subtle" icon={<EditRegular />} onClick={() => { setEditTask(task); setFormOpen(true); }} />
            <Button size="small" appearance="subtle" icon={<DeleteRegular />} style={{ color: tokens.colorStatusDangerForeground1 }} onClick={() => setDeleteTarget(task)} />
          </div>
        ) }] : []),
      ]}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────────

  const tt = t.tasks;
  const overdueCount = allTasks.filter(task => isTaskOverdue(task, todayStr)).length;

  if (loadingTasks) {
    return (
      <div className={s.page}>
        <Spinner label={tt.loading} />
      </div>
    );
  }

  return (
    <div className={s.page}>
      {/* ── Header ── */}
      <div className={s.header}>
        <div>
          <PageTitle>{tt.title}</PageTitle>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {tasks.length} {tasks.length !== 1 ? tt.taskUnitPlural : tt.taskUnit} · {overdueCount} {tt.overdueLabel}
          </Text>
        </div>

        {canWrite && (
          <Button appearance="primary" onClick={() => { setEditTask(null); setFormOpen(true); }}>
            {t.tasks.newTask}
          </Button>
        )}

        {/* Vista toggle */}
        <div className={s.viewToggle}>
          <Tooltip content={tt.viewKanban} relationship="label">
            <Button
              appearance={view === 'kanban' ? 'primary' : 'subtle'}
              size="small"
              icon={<ViewDesktop24Regular />}
              className={s.viewBtn}
              onClick={() => setView('kanban')}
            />
          </Tooltip>
          <Tooltip content={tt.viewList} relationship="label">
            <Button
              appearance={view === 'list' ? 'primary' : 'subtle'}
              size="small"
              icon={<TableRegular />}
              className={s.viewBtn}
              onClick={() => setView('list')}
            />
          </Tooltip>
        </div>
      </div>

      {/* ── Toolbar / filtros ── */}
      <div className={s.toolbar}>
        <Input
          placeholder={tt.searchPlaceholder}
          value={searchText}
          onChange={(_, d) => setSearch(d.value)}
          contentBefore={<FilterRegular />}
          style={{ minWidth: '200px' }}
        />

        <Dropdown
          placeholder={tt.filterAllLots}
          value={zoneOptions.find(o => o.id === filterId)?.label ?? ''}
          selectedOptions={filterId ? [filterId] : []}
          onOptionSelect={(_, d) => {
            const sel = parseLoteZoneId(d.optionValue ?? '', modules);
            if (!sel || d.optionValue === filterId) { setLote(''); setZoneFilter(''); return; }
            setLote(sel.loteId);
            setZoneFilter(sel.moduleId ? (d.optionValue ?? '') : '');
          }}
          style={{ minWidth: '200px' }}
        >
          {zoneOptions.map(o => (
            <Option key={o.id} value={o.id}>{o.label}</Option>
          ))}
        </Dropdown>

        <Dropdown
          placeholder={tt.filterPriority}
          value={prioFilter ? (t.taskPriority as Record<string,string>)[prioFilter] ?? '' : ''}
          selectedOptions={prioFilter ? [prioFilter] : []}
          onOptionSelect={(_, d) => setPrio(d.optionValue === prioFilter ? '' : (d.optionValue ?? ''))}
          style={{ minWidth: '130px' }}
        >
          <Option value="HIGH">{t.taskPriority.HIGH}</Option>
          <Option value="MEDIUM">{t.taskPriority.MEDIUM}</Option>
          <Option value="LOW">{t.taskPriority.LOW}</Option>
        </Dropdown>

        <Dropdown
          placeholder={tt.filterType}
          value={activityLabel[typeFilter] ?? ''}
          selectedOptions={typeFilter ? [typeFilter] : []}
          onOptionSelect={(_, d) => setType(d.optionValue === typeFilter ? '' : (d.optionValue ?? ''))}
          style={{ minWidth: '150px' }}
        >
          {activityTypes.map(type => (
            <Option key={type} value={type}>{activityLabel[type]}</Option>
          ))}
        </Dropdown>

        {(loteFilter || prioFilter || typeFilter || searchText) && (
          <Button
            size="small"
            appearance="subtle"
            onClick={() => { setLote(''); setZoneFilter(''); setPrio(''); setType(''); setSearch(''); }}
          >
            {tt.clearFilters}
          </Button>
        )}
      </div>

      {notice && (
        <Text size={200} role="status" style={{ display: 'block', marginBottom: '12px' }}>{notice}</Text>
      )}

      {/* ── Vista Kanban ── */}
      {view === 'kanban' && (
        <div className={s.kanbanBoard}>
          {STATUS_ORDER.map(status => (
            <div
              key={status}
              className={`${s.column} ${dragOverStatus === status ? s.columnDragOver : ''}`}
              onDragOver={canWrite ? (e) => {
                if (!draggedTaskId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverStatus !== status) setDragOverStatus(status);
              } : undefined}
              onDragLeave={canWrite ? (e) => {
                if (e.currentTarget === e.target) setDragOverStatus(null);
              } : undefined}
              onDrop={canWrite ? (e) => {
                e.preventDefault();
                setDragOverStatus(null);
                const task = tasks.find(t => t.taskId === draggedTaskId);
                setDraggedTaskId(null);
                if (task && task.status !== status) {
                  mutation.mutate({ taskId: task.taskId, loteId: task.loteId, status });
                }
              } : undefined}
            >
              <div className={s.columnHeader}>
                {STATUS_ICON[status]}
                {statusLabel[status]}
                <span className={s.columnCount}>{byStatus[status].length}</span>
              </div>

              {byStatus[status].length === 0 ? (
                <Text size={100} style={{ color: tokens.colorNeutralForeground4, padding: '8px 0' }}>
                  {tt.emptyColumn}
                </Text>
              ) : (
                byStatus[status].map(task => (
                  <TaskCard key={task.taskId} task={task} />
                ))
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Vista Lista — 2 acordeones agrupados por estado (Pendientes/Completados) ── */}
      {view === 'list' && (
        tasks.length === 0 ? (
          <div className={s.emptyState}>{tt.emptyFilter}</div>
        ) : (
          <>
            <div className={s.accordionSection}>
              <button
                type="button"
                className={`${s.accordionHeader} ${s.accordionHeaderPending}`}
                onClick={() => setPendingCollapsed(v => !v)}
              >
                {pendingCollapsed ? <ChevronRight20Regular /> : <ChevronDown20Regular />}
                {tt.groupPending}
                <span className={s.accordionCount}>{pendingTasks.length}</span>
              </button>
              {!pendingCollapsed && (
                pendingTasks.length === 0
                  ? <Text size={100} style={{ color: tokens.colorNeutralForeground4, padding: '8px 14px', display: 'block' }}>{tt.emptyColumn}</Text>
                  : <TaskListTable rows={pendingTasks} />
              )}
            </div>

            <div className={s.accordionSection}>
              <button
                type="button"
                className={`${s.accordionHeader} ${s.accordionHeaderCompleted}`}
                onClick={() => setCompletedCollapsed(v => !v)}
              >
                {completedCollapsed ? <ChevronRight20Regular /> : <ChevronDown20Regular />}
                {tt.groupCompleted}
                <span className={s.accordionCount}>{completedTasks.length}</span>
              </button>
              {!completedCollapsed && (
                completedTasks.length === 0
                  ? <Text size={100} style={{ color: tokens.colorNeutralForeground4, padding: '8px 14px', display: 'block' }}>{tt.emptyColumn}</Text>
                  : <TaskListTable rows={completedTasks} />
              )}
            </div>
          </>
        )
      )}

      <TaskFormDialog
        key={editTask?.taskId ?? 'new-task'}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editTask}
        lotes={lotes}
      />
      <DeleteTaskDialog task={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </div>
  );
}
